import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { CheckBox } from '../../../../components/inputs/CheckBox';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import { Typography } from '../../../../components/display/Typography';
import { HelpTooltip } from '../../../../components/display/HelpTooltip';
import { SpecialTag } from '../../../../components/display/SpecialTag';
import { orderStateVariant } from '../../../../components/display/SpecialTag/orderStateVariant';

// Styles
import { Container, Section, StatesList, StateRow } from './styles';

// GQL
import { client } from '../../../../gql/client';
import { SET_SEGMENT_REVIEW_AUTO_STATES } from '../../../../gql/mutations/segmentReviews';
import { GET_ALL_CONFIGS } from '../../../../gql/queries/appConfig';

// Hooks y utils
import { useSegmentReviewAutoStates } from '../../../../hooks/useSegmentReviewAutoStates';
import { useAllFeatureFlags } from '../../../../hooks/useFeatureFlag';
import { SEGMENT_REVIEW_AUTO_SELECTABLE_STATES } from '../../../../utils/segmentAutoReview';
import { showSuccessToast, showBackendErrorToast } from '../../../../utils/toasts';

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((s) => b.includes(s));

/** Gestión → Revisión de tramos: estados que dan un tramo por revisado solo. */
export default function SegmentReviewSettingsPage(): JSX.Element {
  const { t } = useTranslation(['management', 'common']);
  const savedStates = useSegmentReviewAutoStates();
  // Con la clave ausente el hook devuelve el valor por defecto: se distingue
  // «aún sin mapa» (caché vacía) mirando si el mapa de configuración ya llegó.
  const flags = useAllFeatureFlags();
  const mapLoaded = Object.keys(flags).length > 0;

  // `baseline` = última versión guardada conocida (null hasta que llega la
  // configuración); `selected` = lo que el usuario tiene marcado ahora.
  const [form, setForm] = useState<{
    baseline: string[] | null;
    selected: string[];
  }>({ baseline: null, selected: savedStates });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!mapLoaded) return;
    setForm((current) => {
      // Primera carga: se vuelca una sola vez.
      if (current.baseline === null) {
        return { baseline: savedStates, selected: savedStates };
      }
      if (sameSet(current.baseline, savedStates)) return current;
      // Refresco de fondo: solo se adopta si el usuario no ha tocado nada.
      return {
        baseline: savedStates,
        selected: sameSet(current.selected, current.baseline)
          ? savedStates
          : current.selected,
      };
    });
  }, [savedStates, mapLoaded]);

  const { baseline, selected } = form;
  const ordered = SEGMENT_REVIEW_AUTO_SELECTABLE_STATES.filter((s) =>
    selected.includes(s)
  );
  const dirty = !sameSet(ordered, baseline ?? savedStates);

  const toggle = (state: string) =>
    setForm((current) => ({
      ...current,
      selected: current.selected.includes(state)
        ? current.selected.filter((s) => s !== state)
        : [...current.selected, state],
    }));

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await client.mutate<{
        setSegmentReviewAutoStates?: string[] | null;
      }>({
        mutation: SET_SEGMENT_REVIEW_AUTO_STATES,
        variables: { states: ordered },
        refetchQueries: [{ query: GET_ALL_CONFIGS }],
      });
      // Lo guardado manda ya: así «Guardar» no vuelve a habilitarse mientras
      // llega el refresco de la configuración.
      const saved = data?.setSegmentReviewAutoStates ?? ordered;
      setForm({ baseline: saved, selected: saved });
      showSuccessToast(t('management:segmentReview.saved'));
    } catch (err) {
      showBackendErrorToast(err, t('management:segmentReview.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Section>
        <Typography size="L" variant="semiBold">
          {t('management:segmentReview.title')}
          <HelpTooltip text={t('management:segmentReview.explanation')} />
        </Typography>
        <StatesList>
          {SEGMENT_REVIEW_AUTO_SELECTABLE_STATES.map((state) => {
            const tagVariant = orderStateVariant(state);
            return (
              <StateRow key={state}>
                <CheckBox
                  indeterminate={false}
                  checked={selected.includes(state)}
                  onChange={() => toggle(state)}
                  aria-label={t(`common:states.${state}`)}
                />
                {tagVariant && <SpecialTag sizeTag="medium" variant={tagVariant} />}
              </StateRow>
            );
          })}
        </StatesList>
        {ordered.length === 0 && (
          <Typography size="M">{t('management:segmentReview.off')}</Typography>
        )}
        <MainButton
          variant="primary"
          text={t('common:buttons.save')}
          disabled={!dirty || saving}
          onClick={onSave}
        />
      </Section>
    </Container>
  );
}
